import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as api from "@/lib/api";
import { db } from "@/lib/db";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, RefreshCw, AlertTriangle, Clock, RotateCw, Wrench, X, Sparkles, ShieldCheck, UploadCloud, RotateCcw, CalendarClock } from "lucide-react";
import { formatDateTime } from "@/lib/utils";
import {
  syncNow, getPendingCount, getQueueDetails, retryQueueItem, retryOutboxItem,
  repairSync, discardQueueItem, discardOutboxItem, resolveAllErrors, reconcile, getLastReconcileOk,
} from "@/lib/sync";
import { toast } from "sonner";
import { useOnline } from "@/lib/useOnline";
import { useAuth } from "@/lib/auth";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const ENTITY_LABEL = { animal: "Animal", farm: "Finca", lot: "Lote", event: "Evento", inventory: "Inventario", accion: "Acción" };

export default function SyncSettings() {
  const navigate = useNavigate();
  const location = useLocation();
  const online = useOnline();
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [pending, setPending] = useState(0);
  const [queue, setQueue] = useState({ ops: [], outbox: [] });
  const [running, setRunning] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [autoResolving, setAutoResolving] = useState(false);
  const [animalInfo, setAnimalInfo] = useState({});
  const [farmNames, setFarmNames] = useState({});
  const [reconcileResult, setReconcileResult] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [lastVerified, setLastVerified] = useState(null);
  // Fix D (2026-08-24): reinicio del sistema — solo dueño.
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [resetCounts, setResetCounts] = useState({ animals: 0, events: 0 });
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  // Fix F (2026-09-01): recategorización masiva por nuevos rangos de edad — solo dueño.
  const [recatOpen, setRecatOpen] = useState(false);
  const [recatLoading, setRecatLoading] = useState(false);
  const [recatPreview, setRecatPreview] = useState(null);
  const [recatApplying, setRecatApplying] = useState(false);
  const [recatResult, setRecatResult] = useState(null);

  const load = async () => {
    try { setLogs(await api.listSyncLogs()); } catch (_) {}
    setPending(await getPendingCount());
    setQueue(await getQueueDetails());
    setLastVerified(await getLastReconcileOk());
  };
  useEffect(() => { load(); }, []);

  const runReconcile = async (force = true) => {
    if (!online) return;
    setReconciling(true);
    try {
      const r = await reconcile({ force });
      if (!r.skipped) {
        setReconcileResult(r);
        setLastVerified(r.verifiedAt);
        if (r.requeued > 0) { toast.success(`${r.requeued} registro(s) re-encolado(s) para subir`); await load(); }
      }
    } finally { setReconciling(false); }
  };
  useEffect(() => { runReconcile(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async () => {
    setRunning(true);
    const r = await syncNow();
    toast.success(r?.skipped ? "Sin conexión o sin cambios para sincronizar" : "Sincronizado");
    await load();
    await runReconcile(true);
    setRunning(false);
  };

  const runRepair = async () => {
    if (!online) return toast.error("Necesitás conexión para reparar la sincronización");
    if (!window.confirm("Esto vuelve a traer todo del servidor y sube solo lo que falte. ¿Continuar?")) return;
    setRepairing(true);
    try {
      await api.preOpBackup("Reparar sincronización").catch(() => {}); // §1: respaldo previo, best-effort
      const r = await repairSync();
      if (r?.error) toast.error("Sin conexión — no se pudo reparar");
      else toast.success(`Listo — ${r.reuploaded} registro(s) re-subido(s)`);
      await load();
      runReconcile(true);
    } catch (e) {
      toast.error("No se pudo completar la reparación");
    } finally { setRepairing(false); }
  };

  // Fix 4 (bug de UX — chip duplicado/errores sin acción): reintenta todo lo
  // que quedó con error aplicando la auto-resolución del servidor (chip
  // liberado / duplicados unificados) antes de pedirle al usuario que revise
  // algo a mano.
  const runAutoResolve = async () => {
    if (!online) return toast.error("Necesitás conexión para resolver automáticamente");
    setAutoResolving(true);
    try {
      const r = await resolveAllErrors();
      if (r.attempted === 0) toast.info("No había errores para resolver");
      else if (r.stillErrored === 0) toast.success(`${r.resolved} resuelto(s) automáticamente — ya no quedan errores`);
      else toast.success(`${r.resolved} resuelto(s) automáticamente, ${r.stillErrored} necesitan tu revisión`);
      await load();
    } finally { setAutoResolving(false); }
  };

  const retryOp = async (id) => { await retryQueueItem(id); toast.success("Reintentando…"); load(); };
  const retryAction = async (id) => { await retryOutboxItem(id); toast.success("Reintentando…"); load(); };
  const discardOp = async (id) => { await discardQueueItem(id); toast.success("Descartado"); load(); };
  const discardAction = async (id) => { await discardOutboxItem(id); toast.success("Descartado"); load(); };

  // Fix D (2026-08-24): borrado total (animales/eventos/inventarios), con
  // respaldo previo obligatorio y epoch de datos para forzar a cada
  // dispositivo a descartar su copia local. Solo el dueño la ve/ejecuta.
  const openReset = async () => {
    setResetCounts({ animals: await db.animals.count(), events: await db.events.count() });
    setResetConfirmText("");
    setResetResult(null);
    setResetOpen(true);
  };
  const runReset = async () => {
    if (resetConfirmText !== "REINICIAR") return;
    setResetting(true);
    try {
      const r = await api.systemReset(resetConfirmText);
      setResetResult(r);
      toast.success(`Sistema reiniciado: ${r.total_deleted} registro(s) borrado(s). Queda un respaldo disponible.`);
      await syncNow(); // aplica el data_epoch nuevo también en este dispositivo
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo reiniciar el sistema");
    } finally { setResetting(false); }
  };

  // Fix F (2026-09-01): recategorización masiva — trae la vista previa desde el
  // servidor cada vez que se abre el diálogo (no se cachea, puede haber cambiado).
  const openRecat = async () => {
    setRecatResult(null);
    setRecatOpen(true);
    setRecatLoading(true);
    try {
      setRecatPreview(await api.recategorizePreview());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo obtener la vista previa");
      setRecatOpen(false);
    } finally { setRecatLoading(false); }
  };
  const runRecatApply = async () => {
    setRecatApplying(true);
    try {
      const r = await api.recategorizeApply(true);
      setRecatResult(r);
      toast.success(r.message || "Recategorización aplicada");
      await syncNow(); // refresca el espejo local con las categorías nuevas
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo aplicar la recategorización");
    } finally { setRecatApplying(false); }
  };

  const errorOps = queue.ops.filter((o) => o.status === "error");
  const pendingOps = queue.ops.filter((o) => o.status === "pending");
  const errorOutbox = queue.outbox.filter((o) => o.status === "error");
  const pendingOutbox = queue.outbox.filter((o) => o.status === "pending");

  // Fix 4: cada error de un animal muestra chip/lomo/finca — se resuelve
  // contra el espejo local (Dexie), que conserva esos datos incluso en un
  // borrado (el server sí libera el chip, pero acá solo mostramos info).
  useEffect(() => {
    (async () => {
      const farms = await db.farms.toArray();
      setFarmNames(Object.fromEntries(farms.map((f) => [f.id, f.name])));
      const ids = errorOps.filter((o) => o.entity === "animal").map((o) => o.entity_id);
      const info = {};
      for (const id of new Set(ids)) {
        const a = await db.animals.get(id);
        if (a) info[id] = { chip: a.chip_code || a.chip_code_previous, lomo: a.lomo_number, farmId: a.farm_id };
      }
      setAnimalInfo(info);
    })();
  }, [queue]);

  // Fix 4: tocar el indicador rojo del header trae acá con #errores y hace
  // scroll directo a la lista de errores accionables.
  useEffect(() => {
    if (location.hash === "#errores") {
      const el = document.getElementById("errores");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.hash, errorOps.length, errorOutbox.length]);

  // Desglose por tipo y hora de la op más vieja (Fix 3 — hotfix 31/07/2026):
  // ayuda a ver de un vistazo qué se está atrasando y desde cuándo.
  const breakdown = useMemo(() => {
    const counts = {};
    let oldest = null;
    const consider = (entity, createdAt) => {
      counts[entity] = (counts[entity] || 0) + 1;
      if (!oldest || createdAt < oldest) oldest = createdAt;
    };
    [...queue.ops].forEach((o) => consider(o.entity, o.created_at));
    [...queue.outbox].forEach((o) => consider("accion", o.created_at));
    return { counts, oldest };
  }, [queue]);

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={()=>navigate("/ajustes")} className="h-9 px-2"><ArrowLeft size={18} /></Button>
        <h1 className="font-heading text-2xl font-semibold flex-1">Sincronización</h1>
        <Button onClick={run} disabled={running || !online} className="h-10 bg-emerald-700 hover:bg-emerald-800" data-testid="sync-run-button"><RefreshCw size={14} className="mr-1"/>{running ? "Sincronizando…" : "Sincronizar ahora"}</Button>
      </div>

      <Card>
        <CardContent className="p-5 flex items-center justify-between gap-3">
          <div>
            <div className="text-sm">Pendientes en cola local: <span className="font-semibold" data-testid="sync-pending-count">{pending}</span></div>
            <div className="text-xs text-muted-foreground mt-0.5">{online ? "Con señal — se sincroniza automáticamente cada 30s." : "Sin conexión — se sincronizará al volver la señal."}</div>
          </div>
          {(errorOps.length + errorOutbox.length) > 0 && (
            <Badge variant="destructive" className="gap-1"><AlertTriangle size={12}/>{errorOps.length + errorOutbox.length} con error</Badge>
          )}
        </CardContent>
      </Card>

      {pending > 0 && (
        <Card data-testid="sync-breakdown-card">
          <CardHeader><CardTitle className="text-base">Desglose de pendientes</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {Object.entries(breakdown.counts).map(([entity, count]) => (
                <Badge key={entity} variant="outline" className="font-mono" data-testid={`sync-breakdown-${entity}`}>
                  {ENTITY_LABEL[entity] || entity}: {count}
                </Badge>
              ))}
            </div>
            {breakdown.oldest && (
              <div className="text-xs text-muted-foreground" data-testid="sync-breakdown-oldest">
                La operación más antigua sin subir es de {formatDateTime(breakdown.oldest)}.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Verificación de integridad dispositivo <-> servidor (§2) */}
      <Card data-testid="reconcile-card">
        <CardHeader className="flex-row items-center justify-between gap-2 space-y-0">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck size={16} className={reconcileResult?.allMatch ? "text-emerald-600" : "text-amber-600"} />
            Integridad de los datos
          </CardTitle>
          <Button size="sm" variant="outline" onClick={() => runReconcile(true)} disabled={reconciling || !online} data-testid="reconcile-run-button">
            <RefreshCw size={13} className={`mr-1 ${reconciling ? "animate-spin" : ""}`} />{reconciling ? "Verificando…" : "Verificar ahora"}
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {!online && <p className="text-sm text-muted-foreground">Necesitás conexión para verificar contra el servidor.</p>}
          {online && !reconcileResult && <p className="text-sm text-muted-foreground">Verificando…</p>}
          {online && reconcileResult && (
            <div className="space-y-2" data-testid="reconcile-result">
              {reconcileResult.allMatch ? (
                <p className="text-sm" data-testid="reconcile-status-ok">
                  <span className="font-mono font-semibold">{reconcileResult.entities?.animal?.local ?? 0}</span> animales en este equipo y{" "}
                  <span className="font-mono font-semibold">{reconcileResult.entities?.animal?.server ?? 0}</span> en el servidor. Todo respaldado.
                </p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(reconcileResult.entities || {}).filter(([, v]) => !v.match).map(([entity, v]) => (
                    <div key={entity} className="flex items-center justify-between gap-2 text-sm" data-testid={`reconcile-mismatch-${entity}`}>
                      <span>
                        {v.missing_on_server > 0
                          ? <>{v.missing_on_server} {ENTITY_LABEL[entity] || entity}(s) de este equipo todavía no están en el servidor</>
                          : <>{ENTITY_LABEL[entity] || entity}: {v.local} en este equipo, {v.server} en el servidor</>}
                      </span>
                    </div>
                  ))}
                  <Button size="sm" onClick={() => runReconcile(true)} disabled={reconciling} data-testid="reconcile-upload-now-button">
                    <UploadCloud size={13} className="mr-1"/>Subir ahora
                  </Button>
                </div>
              )}
              <div className="text-xs text-muted-foreground" data-testid="reconcile-last-verified">
                Última verificación exitosa: {lastVerified ? formatDateTime(lastVerified) : "todavía no se pudo verificar del todo"}.
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5 flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium">Reparar sincronización</div>
            <div className="text-xs text-muted-foreground mt-0.5">Vuelve a traer todo del servidor y sube solo lo que falte. Usalo si notás animales o cambios que "desaparecen" al sincronizar.</div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={runRepair}
            disabled={repairing || !online}
            data-testid="sync-repair-button"
          >
            <Wrench size={13} className="mr-1"/>{repairing ? "Reparando…" : "Reparar"}
          </Button>
        </CardContent>
      </Card>

      {(errorOps.length > 0 || errorOutbox.length > 0) && (
        <Card className="border-destructive/40" id="errores">
          <CardHeader className="flex-row items-center justify-between gap-2 space-y-0">
            <CardTitle className="text-base text-destructive flex items-center gap-2"><AlertTriangle size={16}/>Con error — necesitan revisión</CardTitle>
            <Button
              size="sm"
              variant="outline"
              className="shrink-0"
              onClick={runAutoResolve}
              disabled={autoResolving || !online}
              data-testid="sync-auto-resolve-button"
            >
              <Sparkles size={13} className="mr-1"/>{autoResolving ? "Resolviendo…" : "Resolver automáticamente"}
            </Button>
          </CardHeader>
          <CardContent className="p-0 divide-y divide-border">
            {errorOps.map((o) => {
              const info = o.entity === "animal" ? animalInfo[o.entity_id] : null;
              return (
                <div key={`op-${o.id}`} className="p-3 text-sm flex items-center gap-2" data-testid={`sync-error-op-${o.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{ENTITY_LABEL[o.entity] || o.entity} · {o.operation}</div>
                    {info && (
                      <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-x-3" data-testid={`sync-error-op-${o.id}-info`}>
                        <span>Chip: <span className="font-mono">{info.chip || "—"}</span></span>
                        <span>Lomo: {info.lomo || "—"}</span>
                        <span>Finca: {farmNames[info.farmId] || "—"}</span>
                      </div>
                    )}
                    <div className="text-xs text-destructive/90 mt-0.5">{o.last_error || "Error desconocido"}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={()=>retryOp(o.id)} data-testid={`sync-retry-op-${o.id}`}><RotateCw size={13} className="mr-1"/>Reintentar</Button>
                  <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={()=>discardOp(o.id)} data-testid={`sync-discard-op-${o.id}`}><X size={13} className="mr-1"/>Descartar</Button>
                </div>
              );
            })}
            {errorOutbox.map((o) => (
              <div key={`ob-${o.id}`} className="p-3 text-sm flex items-center gap-2" data-testid={`sync-error-outbox-${o.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">Acción · {o.type}</div>
                  <div className="text-xs text-destructive/90 mt-0.5">{o.last_error || "Error desconocido"}</div>
                </div>
                <Button size="sm" variant="outline" onClick={()=>retryAction(o.id)} data-testid={`sync-retry-outbox-${o.id}`}><RotateCw size={13} className="mr-1"/>Reintentar</Button>
                <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={()=>discardAction(o.id)} data-testid={`sync-discard-outbox-${o.id}`}><X size={13} className="mr-1"/>Descartar</Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {(pendingOps.length > 0 || pendingOutbox.length > 0) && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Clock size={16}/>En cola</CardTitle></CardHeader>
          <CardContent className="p-0 divide-y divide-border">
            {pendingOps.map((o) => (
              <div key={`op-${o.id}`} className="p-3 text-xs flex items-center gap-2">
                <span className="font-mono px-1.5 py-0.5 rounded bg-muted text-foreground">{ENTITY_LABEL[o.entity] || o.entity}</span>
                <span className="font-medium capitalize">{o.operation}</span>
                <span className="ml-auto text-muted-foreground">{formatDateTime(o.created_at)}</span>
              </div>
            ))}
            {pendingOutbox.map((o) => (
              <div key={`ob-${o.id}`} className="p-3 text-xs flex items-center gap-2">
                <span className="font-mono px-1.5 py-0.5 rounded bg-muted text-foreground">acción</span>
                <span className="font-medium capitalize">{o.type}</span>
                <span className="ml-auto text-muted-foreground">{formatDateTime(o.created_at)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Audit log (servidor)</CardTitle></CardHeader>
        <CardContent className="p-0 divide-y divide-border">
          {logs.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Sin actividad reciente{!online && " (sin conexión)"}.</p>}
          {logs.map((l) => (
            <div key={l.id} className="p-3 text-xs flex items-center gap-2">
              <span className="font-mono px-1.5 py-0.5 rounded bg-muted text-foreground">{l.entity}</span>
              <span className="font-medium capitalize">{l.operation}</span>
              <span className="text-muted-foreground">{l.entity_id.slice(0, 8)}…</span>
              <span className="ml-auto text-muted-foreground">{formatDateTime(l.server_received_at)}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Fix F (2026-09-01): recategorización masiva por los nuevos rangos de
          edad (Prompt E) — solo dueño. No toca categorías manuales ni bufalinos. */}
      {user?.role === "owner" && (
        <Card data-testid="recategorize-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><CalendarClock size={16}/>Actualizar grupos etarios</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Recalcula la categoría (becerra/mauta/novilla/vaca, becerro/maute/toro) de los animales cargados con las reglas de edad anteriores. Solo se tocan los que quedaron asignados automáticamente — nunca una categoría puesta a mano ni bufalinos.
            </p>
            <Button variant="outline" size="sm" onClick={openRecat} data-testid="recategorize-open-button">
              <CalendarClock size={13} className="mr-1"/>Revisar grupos etarios
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Fix D (2026-08-24): reinicio del sistema — la operación más destructiva
          de la plataforma, solo visible/ejecutable por el dueño. */}
      {user?.role === "owner" && (
        <Card className="border-destructive/50" data-testid="system-reset-card">
          <CardHeader>
            <CardTitle className="text-base text-destructive flex items-center gap-2"><RotateCcw size={16}/>Reiniciar sistema</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Borra todos los animales, eventos e inventarios para empezar de cero. Se conservan las fincas, los lotes y los usuarios. Se genera un respaldo antes de borrar.
            </p>
            <Button variant="destructive" size="sm" onClick={openReset} data-testid="system-reset-button">
              <RotateCcw size={13} className="mr-1"/>Reiniciar sistema
            </Button>
          </CardContent>
        </Card>
      )}

      <AlertDialog open={resetOpen} onOpenChange={(o) => { if (!resetting) setResetOpen(o); }}>
        <AlertDialogContent data-testid="system-reset-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reiniciar sistema</AlertDialogTitle>
            <AlertDialogDescription>
              {resetResult ? (
                <>Listo. Se borraron {resetResult.total_deleted} registro(s). Queda un respaldo previo disponible en Ajustes → Backups.</>
              ) : (
                <>
                  Se van a borrar <span className="font-semibold">{resetCounts.animals} animal(es)</span> y{" "}
                  <span className="font-semibold">{resetCounts.events} evento(s)</span>. Se conservan fincas, lotes y usuarios.
                  Esta acción no se puede deshacer. Escribí <span className="font-mono font-semibold">REINICIAR</span> para confirmar.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {!resetResult && (
            <div className="py-1">
              <Label className="text-xs text-muted-foreground">Escribí REINICIAR para confirmar</Label>
              <Input
                value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value)}
                placeholder="REINICIAR"
                className="mt-1"
                data-testid="system-reset-confirm-input"
              />
            </div>
          )}
          <AlertDialogFooter>
            {resetResult ? (
              <AlertDialogCancel onClick={() => setResetOpen(false)}>Cerrar</AlertDialogCancel>
            ) : (
              <>
                <AlertDialogCancel disabled={resetting}>Cancelar</AlertDialogCancel>
                <Button
                  variant="destructive"
                  disabled={resetConfirmText !== "REINICIAR" || resetting}
                  onClick={runReset}
                  data-testid="system-reset-confirm-button"
                >
                  {resetting ? "Reiniciando…" : "Reiniciar sistema"}
                </Button>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Fix F (2026-09-01): vista previa + aplicación de la recategorización masiva. */}
      <AlertDialog open={recatOpen} onOpenChange={(o) => { if (!recatApplying) setRecatOpen(o); }}>
        <AlertDialogContent data-testid="recategorize-dialog" className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Actualizar grupos etarios</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {recatLoading && <p className="text-sm">Calculando…</p>}
                {!recatLoading && recatResult && (
                  <p className="text-sm" data-testid="recategorize-applied-message">
                    {recatResult.message}
                  </p>
                )}
                {!recatLoading && !recatResult && recatPreview && (
                  <div className="space-y-3 text-sm" data-testid="recategorize-preview">
                    <p>
                      Se evaluaron <span className="font-semibold">{recatPreview.total_evaluados}</span> animal(es).{" "}
                      <span className="font-semibold text-foreground">{recatPreview.cambiarian.cantidad}</span> cambiarían de categoría.
                    </p>
                    {recatPreview.cambiarian.cantidad > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(recatPreview.cambiarian.desglose).map(([k, v]) => (
                          <Badge key={k} variant="outline" className="font-mono" data-testid={`recategorize-breakdown-${k}`}>
                            {k}: {v}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {recatPreview.muestra.length > 0 && (
                      <div className="border border-border rounded-md divide-y divide-border max-h-48 overflow-y-auto" data-testid="recategorize-sample-list">
                        {recatPreview.muestra.map((it, i) => (
                          <div key={i} className="p-2 text-xs flex items-center gap-2">
                            <span className="font-mono">{it.chip_code || "—"}</span>
                            <span className="ml-auto">{it.categoria_actual} → <span className="font-semibold text-foreground">{it.categoria_nueva}</span></span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      Sin fecha de nacimiento: {recatPreview.sin_fecha} · Bufalinos (no se tocan): {recatPreview.bufalinos} · Categoría manual (no se tocan): {recatPreview.categoria_manual}
                    </div>
                    {recatPreview.cambiarian.cantidad === 0 && (
                      <p className="text-muted-foreground">No hay animales para actualizar — todo ya está al día con las reglas nuevas.</p>
                    )}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {recatResult ? (
              <AlertDialogCancel onClick={() => setRecatOpen(false)}>Cerrar</AlertDialogCancel>
            ) : (
              <>
                <AlertDialogCancel disabled={recatApplying}>Cancelar</AlertDialogCancel>
                <Button
                  variant="default"
                  disabled={recatLoading || recatApplying || !recatPreview || recatPreview.cambiarian.cantidad === 0}
                  onClick={runRecatApply}
                  data-testid="recategorize-apply-button"
                >
                  {recatApplying ? "Aplicando…" : `Aplicar (${recatPreview?.cambiarian.cantidad ?? 0})`}
                </Button>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
